import {
  Divider,
  FormInstance,
  InputNumber,
  Space,
  Switch,
  Tooltip,
  Typography,
} from "antd";
import { Cable, Check, ClipboardType, FileText, Image, Images, Info, ListChecks, ListTodo } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MyFormItem,
  MyFormItemGroup,
} from "../../../custom-form-item/CustomFormItem";
import { ExamGroup, ExamQuestionType } from "@types";
import { useGetExamGroupQuestions } from "@hooks";

interface Props {
  form: FormInstance<any>;
  examGroup: ExamGroup;
}

export const QuestionsSelectRandom = ({ form, examGroup }: Props) => {
  const [numberOfQuestions, setNumberOfQuestions] = useState(10);
  const { t } = useTranslation();
  const { data } = useGetExamGroupQuestions(examGroup.id);
  const [numberOfQuestionTypes] = useState({
    singleChoice: data?.items.filter((x) => x.questionType.id === ExamQuestionType.SingleChoice)?.length || 0,
    multipleChoice: data?.items.filter((x) => x.questionType.id === ExamQuestionType.MultipleChoices)?.length || 0,
    correctIncorrect: data?.items.filter((x) => x.questionType.id === ExamQuestionType.CorrectIncorrect)?.length || 0,
    shortAnswer: data?.items.filter((x) => x.questionType.id === ExamQuestionType.ShortAnswer)?.length || 0,
    essay: data?.items.filter((x) => x.questionType.id === ExamQuestionType.EssayQuestion)?.length || 0,
    matchItems: data?.items.filter((x) => x.questionType.id === ExamQuestionType.Connecting)?.length || 0,
    imageSingleChoice: data?.items.filter((x) => x.questionType.id === ExamQuestionType.ImageChoice)?.length || 0,
    imageMultiChoice: data?.items.filter((x) => x.questionType.id === ExamQuestionType.ImageMultipleChoice)?.length || 0,
  });
  const onChange = () => {

    const number =
      form.getFieldValue(["examGroupProperties", "numberOSingleChoiceQuestions"]) +
      form.getFieldValue(["examGroupProperties", "numberOMultipleChoiceQuestions"]) +
      form.getFieldValue(["examGroupProperties", "numberOCorrectIncorrectQuestions"]) +
      form.getFieldValue(["examGroupProperties", "numberOShortAnswerQuestions"]) +
      form.getFieldValue(["examGroupProperties", "numberOfEssayQuestions"]) +
      form.getFieldValue(["examGroupProperties", "numberOfMatchItemsQuestions"]) +
      form.getFieldValue(["examGroupProperties", "numberOfImageSingleChoiceQuestions"]) +
      form.getFieldValue(["examGroupProperties", "numberOfImageMultiChoiceQuestions"]);
    setNumberOfQuestions(number);
  };

  useEffect(() => {
    onChange();
  }, []);

  return (
    <>
      <MyFormItemGroup prefix={"examGroupProperties"}>
        <Space wrap>
          <MyFormItem
            initialValue={0}
            name={["numberOSingleChoiceQuestions"]}
            label={t("forms.questionGroup.singleChoice")}
          >
            <InputNumber min={0} onChange={onChange} prefix={<ListTodo />} max={numberOfQuestionTypes.singleChoice} />
          </MyFormItem>
          <MyFormItem
            initialValue={0}
            name={["numberOMultipleChoiceQuestions"]}
            label={t("forms.questionGroup.multipleChoices")}
          >
            <InputNumber min={0} onChange={onChange} prefix={<ListChecks />} max={numberOfQuestionTypes.multipleChoice} />
          </MyFormItem>
          <MyFormItem
            initialValue={0}
            name={["numberOCorrectIncorrectQuestions"]}
            label={t("forms.questionGroup.correctIncorrect")}
          >
            <InputNumber min={0} onChange={onChange} prefix={<Check />} max={numberOfQuestionTypes.correctIncorrect} />
          </MyFormItem>
          <MyFormItem
            initialValue={0}
            name={["numberOShortAnswerQuestions"]}
            label={t("forms.questionGroup.shortAnswer")}
          >
            <InputNumber
              min={0}
              onChange={onChange}
              prefix={<ClipboardType />}
              max={numberOfQuestionTypes.shortAnswer}
            />
          </MyFormItem>
          <MyFormItem
            initialValue={0}
            name={["numberOfEssayQuestions"]}
            label={t("pages.bankPool.essay")}
          >
            <InputNumber
              min={0}
              onChange={onChange}
              prefix={<FileText />}
              max={numberOfQuestionTypes.essay}
            />
          </MyFormItem>
          <MyFormItem
            initialValue={0}
            name={["numberOfMatchItemsQuestions"]}
            label={t("pages.bankPool.connecting")}
          >
            <InputNumber
              min={0}
              onChange={onChange}
              prefix={<Cable />}
              max={numberOfQuestionTypes.matchItems}
            />
          </MyFormItem>
          <MyFormItem
            initialValue={0}
            name={["numberOfImageSingleChoiceQuestions"]}
            label={t("pages.bankPool.imageChoice")}
          >
            <InputNumber
              min={0}
              onChange={onChange}
              prefix={<Image />}
              max={numberOfQuestionTypes.imageSingleChoice}
            />
          </MyFormItem>
          <MyFormItem
            initialValue={0}
            name={["numberOfImageMultiChoiceQuestions"]}
            label={t("pages.bankPool.multipleImageChoice")}
          >
            <InputNumber
              min={0}
              onChange={onChange}
              prefix={<Images />}
              max={numberOfQuestionTypes.imageMultiChoice}
            />
          </MyFormItem>
        </Space>
      </MyFormItemGroup>

      <Divider style={{ margin: "0 auto 8px auto" }} />

      <MyFormItemGroup prefix={"examGroupProperties"}>
        <MyFormItem
          initialValue={false}
          name={["placeEssayQuestionsAtEnd"]}
          valuePropName="checked"
          label={(
            <Space size={6}>
              <span>{t("forms.questionGroup.essayLast")}</span>
              <Tooltip title={t("forms.questionGroup.essayLastTooltip")}>
                <span style={{ display: "inline-flex", alignItems: "center" }}>
                  <Info size={14} />
                </span>
              </Tooltip>
            </Space>
          )}
        >
          <Switch />
        </MyFormItem>
      </MyFormItemGroup>

      <Typography.Text strong>{numberOfQuestions}</Typography.Text>
    </>
  );
};
